import { useEffect, useRef } from 'react';
import { useGame } from 'context/GameContext';
import { Clock as ClockConfig } from 'config';
import clock from 'util/clock';
import ambient from 'model/ambient';
import walkUpkeep from 'model/actions/walkUpkeep';
import Debug from 'debug';

const debug = Debug('hooks:useGameUpdate');

const WALK_PHASE = 'path';

// How often a town says anything at all, as a share of its phases. The road speaks every
// phase — the country changes under the player and nothing else is writing to the log
// between steps — but a town is one place standing still, with beg / work / rob / sleep
// each putting their own lines up, and speaking at road rate there read as chatter. It also
// burned through a six-line bag fast enough that the repeats showed. This is the knob: raise
// it if a town goes quiet, lower it if it talks over the player.
//
// This roll is now the only thing standing between a town and its own noise. The light turning
// over used to write on a separate, ungated channel, and since every phase change crosses the
// light it landed on every phase regardless of what this said — the gate was real and the town
// was still loud. Those lines live in model/ambient.js now and come through here like the rest,
// which means a crossover the roll silences passes with only the page fade to mark it. That is
// the trade; raise this first if the light starts changing for no reason the player can see.
const TOWN_AMBIENT_CHANCE = 0.4;

const inWalk = state => state.party?.phase?.name === WALK_PHASE;

// The town, not the city *phase*. party.phase.name stays 'city' for the rest of the game
// after the first arrival — handlers.js:24 is the only live nextPhase() — so the phase name
// says nothing about where the player is standing. party.city is what puts the town screen
// on (locations.js:32), so it is what scopes the clock, and it generalises to every later
// town for free. Checked after inWalk because party.city is already set at walk step 12,
// before ARRIVE flips the phase.
const inTown = state => !inWalk(state) && !!state.party?.city;

/**
 * The game clock.
 *
 * Advances the calendar's day/night phase on a wall-clock schedule, so time passes whether or
 * not the player clicks. Runs in two places, and they cost different things:
 *
 *  - the walk phase, where it flips day/night and deliberately does *not* count days. See
 *    docs/tick-loop.md for what the day-change pipeline detonates.
 *  - a town, where it does count days, because that is what makes the town's arrivals (the
 *    healer, the drunk, the rancher) turn up on their own and the daily rations get eaten.
 *
 * The open road is still untimed.
 *
 * Why it is built this way:
 *  - setInterval over wall-clock deltas, not mainloop.js. mainloop's delta is a simulated
 *    fixed timestep, so a throttled tab silently loses in-game time.
 *  - registered once; never re-registered on a state change.
 *  - reads state through a ref, never the effect closure.
 *  - mutates the calendar outside any setState updater. StrictMode double-invokes updater
 *    functions in React 17, so a mutation inside one would apply twice in dev.
 */
const useGameUpdate = () => {
  const { gameState, setGameState, addNotification } = useGame();

  // Mirror of the latest committed state. No dep array: refreshed after every commit.
  const stateRef = useRef(gameState);
  useEffect(() => {
    stateRef.current = gameState;
  });

  useEffect(() => {
    const config = ClockConfig();
    const budget = config.phaseMs * config.maxCatchUpPhases;

    let last = Date.now();
    let accumulator = 0;

    // At most one ambient line per phase, at a jittered point somewhere inside it. It used to
    // run on its own wall-clock interval, which put three lines in a phase and read as weather
    // noise — worst in a town, where the bags are short enough that the repeats show. Tying it
    // to the accumulator means the prose can never outrun the world it is describing.
    let ambientAt = 0;
    let ambientDone = false;

    // Schedules the line into what is left of the current phase, so it never lands on the
    // heels of one the player just caused. Called on a phase boundary (where the remainder
    // is the whole phase) and by anything that has just written to the log itself — see
    // walk/index.js. Late in a phase this can leave no room, and that phase stays quiet.
    //
    // silent skips the phase outright. Two things ask for that, both of them town-only: the
    // TOWN_AMBIENT_CHANCE roll, and a phase the player spent acting — a town has nothing to
    // say to someone who is busy in it, and beg / work / rob / sleep have each just written a
    // line of their own.
    const resetAmbient = ({ silent = false } = {}) => {
      const remaining = Math.max(0, config.phaseMs - accumulator);
      ambientAt = accumulator + remaining * (0.3 + Math.random() * 0.5);
      ambientDone = silent;
    };

    // The road speaks every phase; a town only sometimes. See TOWN_AMBIENT_CHANCE.
    const townIsQuiet = () => inTown(stateRef.current) && Math.random() >= TOWN_AMBIENT_CHANCE;

    // Schedule the first one; otherwise ambientAt sits at 0 and the opening tick fires a
    // line before the player has taken a step.
    resetAmbient();

    // Called after an action burns a phase — beg / work / rob / sleep. The phase now starting
    // is one the player bought with a click, so in a town it stays quiet.
    const reset = () => {
      accumulator = 0;
      resetAmbient({ silent: inTown(stateRef.current) });
      last = Date.now();
    };

    // Frozen while an event or combat is on screen. Combat needs no separate check: it only
    // renders from inside Event.js when currentEvent.enemies is set.
    const canTick = state =>
      !!state.map &&
      (inWalk(state) || inTown(state)) &&
      !clock.isPaused() &&
      !state.currentEvent &&
      state.eventQueue.length === 0;

    const advance = phases => {
      const state = stateRef.current;
      const { calendar } = state;

      for (let i = 0; i < phases; i++) {
        if (inTown(state)) {
          // nextStep counts the day on the night->day boundary and runs the day-change
          // listeners. That is the whole point in town: the date moves, rations are eaten,
          // and the healer arrives because the day arrived rather than because of a click.
          calendar.nextStep(state);
        } else {
          calendar.advancePhase();
        }

        // The walk phase is a race against one in-game day; health is the timer.
        if (walkUpkeep.enabled(state)) {
          const { notification } = walkUpkeep.execute(state);
          if (notification) addNotification(notification);
        }
      }

      // Dying queues the death modal, which freezes the clock via canTick. Pausing as well
      // means a queued event can never resume time behind the modal.
      if (state.party.resources.attribs.health.value <= 0) clock.pause();

      // calendar mutates in place, so a new top-level reference is the only thing that will
      // re-render Header / HeaderResources. Load-bearing in town too: City.js re-derives its
      // action list off gameState, and that is what enables Rob and disables Work when the
      // clock flips to night under a player who has not touched anything.
      setGameState(prev => ({
        ...prev,
        tick: (prev.tick || 0) + 1,
        isNight: calendar.isNight()
      }));
    };

    const step = ({ force = false, phases = null, elapsed = null } = {}) => {
      const now = Date.now();
      const delta = elapsed === null ? now - last : elapsed;
      last = now;

      if (!force && !canTick(stateRef.current)) {
        // Freeze: return before banking elapsed time, but keep progress toward the current
        // phase, so a player can't stall the clock by opening an event every 29 seconds.
        return 0;
      }

      if (phases !== null) {
        advance(phases);
        return phases;
      }

      accumulator = Math.min(accumulator + delta, budget);

      let due = 0;
      while (accumulator >= config.phaseMs) {
        accumulator -= config.phaseMs;
        due++;
      }

      if (due) {
        debug('tick', due, 'phase(s)');
        advance(due);
        // A phase advance may already have written a line through walkUpkeep. Reschedule
        // into the new phase rather than stacking a second line on top of it.
        resetAmbient({ silent: townIsQuiet() });
        return due;
      }

      // Once inside the phase, the world says one thing on its own. Independent of the
      // player's Walk, and of whether they are clicking at all.
      if (config.ambientEnabled && !ambientDone && accumulator >= ambientAt) {
        ambientDone = true;
        const state = stateRef.current;
        addNotification(ambient(state.calendar.isNight(), inTown(state) ? 'town' : 'road'));
      }

      return 0;
    };

    clock.attach({
      reset,
      resetAmbient: () => resetAmbient({ silent: townIsQuiet() }),
      // Force a phase through, ignoring the gate. Only the debug panel uses this.
      advance: (n = 1) => step({ phases: n, force: true }),
      inspect: () => {
        const state = stateRef.current;
        return {
          accumulator,
          phaseMs: config.phaseMs,
          budget,
          enabled: config.enabled,
          paused: clock.isPaused(),
          canTick: canTick(state),
          day: state.calendar?.step ?? 0,
          isNight: !!state.calendar?.isNight(),
          // Broken out so the debug panel can say *which* clause is holding time.
          gates: {
            hasMap: !!state.map,
            phase: state.party?.phase?.name,
            city: state.party?.city ?? null,
            inWalkPhase: inWalk(state),
            inTown: inTown(state),
            countsDays: inTown(state),
            currentEvent: state.currentEvent?.title ?? null,
            queued: state.eventQueue?.length ?? 0
          }
        };
      }
    });

    if (config.exposeForTests) {
      window.__westClock = {
        // Runs the same canTick gate as the interval, so the gating itself is under test.
        tick: (n = 1) => step({ phases: n }),
        forceTick: (n = 1) => step({ phases: n, force: true }),
        // Feeds the real accumulator/clamp path with a fixed elapsed time instead of wall time.
        elapse: ms => step({ elapsed: ms }),
        config: () => ({
          dayMs: config.dayMs,
          phaseMs: config.phaseMs,
          budget,
          deadlinePhases: config.deadlinePhases
        }),
        pause: () => clock.pause(),
        resume: () => clock.resume(),
        inspect: () => clock.inspect()
      };
    }

    const id = config.enabled ? setInterval(step, config.tickMs) : null;

    return () => {
      if (id) clearInterval(id);
      clock.detach();
      delete window.__westClock;
    };
  }, [setGameState, addNotification]);
};

export default useGameUpdate;
